import { Navigate, useLocation, type Location } from 'react-router';
import type { ReactNode } from 'react';
import { Spinner } from '@ircf/ui';
import type { AppRole } from '@ircf/contracts';
import { landingPathForRole } from '@ircf/config';
import { useSession } from './session.js';

/**
 * Client-side route guards.
 *
 * These exist purely so an unauthorised user sees a sign-in screen instead of a
 * flash of broken UI. They are *not* a security boundary: every guarded route
 * fetches its data from endpoints that independently enforce role, application
 * membership and record ownership server-side. Bypassing a guard in the browser
 * yields an empty screen and a 403, never data.
 */

function FullPageSpinner({ label }: { label: string }): JSX.Element {
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center gap-4"
      role="status"
      aria-live="polite"
    >
      <Spinner size="lg" />
      <p className="text-sm text-cool-steel">{label}</p>
    </div>
  );
}

/** Preserves the attempted destination so sign-in can return the user to it. */
function loginRedirect(location: Location): string {
  const target = `${location.pathname}${location.search}`;
  return `/login?next=${encodeURIComponent(target)}`;
}

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { isResolving, isAuthenticated } = useSession();
  const location = useLocation();

  if (isResolving) return <FullPageSpinner label="Checking your session…" />;
  if (!isAuthenticated) return <Navigate to={loginRedirect(location)} replace />;

  return <>{children}</>;
}

/**
 * Requires one of the supplied roles. A signed-in user with the wrong role is
 * sent to their own landing page rather than to sign-in, which would be
 * confusing - they are authenticated, just not authorised for this area.
 */
export function RequireRole({
  roles,
  children,
}: {
  roles: readonly AppRole[];
  children: ReactNode;
}): JSX.Element {
  const { isResolving, isAuthenticated, role, hasRole } = useSession();
  const location = useLocation();

  if (isResolving) return <FullPageSpinner label="Checking your access…" />;
  if (!isAuthenticated) return <Navigate to={loginRedirect(location)} replace />;
  if (!hasRole(...roles)) {
    return <Navigate to={landingPathForRole(role ?? 'client')} replace />;
  }

  return <>{children}</>;
}

/**
 * Holds a client inside onboarding until screening and consents are complete.
 *
 * The onboarding route itself is exempt, as are staff, who have no onboarding
 * flow. This mirrors the backend rule that a training booking cannot be created
 * before essential onboarding is recorded - enforcing it here means the client
 * meets a guided form rather than a rejected request.
 */
export function RequireOnboarding({ children }: { children: ReactNode }): JSX.Element {
  const { isResolving, isAuthenticated, requiresOnboarding, hasRole } = useSession();
  const location = useLocation();

  if (isResolving) return <FullPageSpinner label="Loading your portal…" />;
  if (!isAuthenticated) return <Navigate to={loginRedirect(location)} replace />;

  const isStaff = hasRole('coach', 'client_admin', 'system_admin');
  const onOnboardingRoute = location.pathname.includes('/portal/onboarding');
  const onFormsRoute = location.pathname.includes('/portal/forms');

  if (!isStaff && requiresOnboarding && !onOnboardingRoute && !onFormsRoute) {
    return <Navigate to="/portal/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * Holds a newly registered client on a waiting screen until the trainer
 * accepts the account. They cannot reach onboarding, plans or bookings yet.
 */
export function RequireApproval({ children }: { children: ReactNode }): JSX.Element {
  const { isResolving, isAuthenticated, requiresApproval, hasRole } = useSession();
  const location = useLocation();

  if (isResolving) return <FullPageSpinner label="Loading your portal…" />;
  if (!isAuthenticated) return <Navigate to={loginRedirect(location)} replace />;

  const isStaff = hasRole('coach', 'client_admin', 'system_admin');
  const onWaitingRoute = location.pathname.includes('/portal/awaiting-approval');

  if (!isStaff && requiresApproval && !onWaitingRoute) {
    return <Navigate to="/portal/awaiting-approval" replace />;
  }
  if (!isStaff && !requiresApproval && onWaitingRoute) {
    return <Navigate to="/portal/onboarding" replace />;
  }

  return <>{children}</>;
}

/**
 * For sign-in and registration: redirects an already-authenticated user to their
 * landing page, honouring a `next` parameter when it points somewhere in-app.
 */
export function RedirectIfAuthenticated({ children }: { children: ReactNode }): JSX.Element {
  const { isResolving, isAuthenticated, role } = useSession();
  const location = useLocation();

  if (isResolving) return <FullPageSpinner label="Checking your session…" />;

  if (isAuthenticated) {
    const next = new URLSearchParams(location.search).get('next');
    // Only accept a same-origin app path; an absolute URL here would be an open
    // redirect.
    const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : null;
    return <Navigate to={safeNext ?? landingPathForRole(role ?? 'client')} replace />;
  }

  return <>{children}</>;
}
